import HttpHelper from "../common/HttpHelper";

import BaseServers from "./BaseServers";
import Config from "../config";
import WxApi from "../common/wxapi";
import JSEncrypt from 'jsencrypt';

const APPEAL_PUBLIC_KEY = `-----BEGIN PUBLIC KEY-----
MIGfMA0GCSqGSIb3DQEBAQUAA4GNADCBiQKBgQCjI8E8LT0fwFekelMMuTWuaIfo
fK69lyNIo+Vz0CGdfE3rLSIH94S2A3Q+bg+9/VnImvfXzcDVmqwHwC4hHPHs6hc6
ufq0gfivTPms3kwX74F5qLMr70j4iZLt/PCkU+uyQ56KmRW4foCV4RPX8o8QZVss
6eifHaeUeJxKM556ewIDAQAB
-----END PUBLIC KEY-----`;

// item_detail（多项目/预约/选座明细）请求去重与失败兜底
// 背景：活动详情页（/detail）跳转报名表单（/enrollForm）时两处都会请求该接口，
// 来回快速切换时同一份数据被反复请求，会触发服务端频控（sta=700），
// 导致表单页拿不到项目数据、无法提交。这里做三层保护：
// 1. 参数完全相同的请求在短窗口内直接复用结果（不同 reserve_month / item_key 照常请求）；
// 2. 首次命中频控且无可用兜底时，间隔一段时间重试一次；
// 3. 请求最终失败时用最近一次成功结果兜底，保证表单页项目列表不被清空。
const ITEM_DETAIL_CACHE_TTL = 4000; // 相同参数结果直接复用的时间窗口
const ITEM_DETAIL_FALLBACK_TTL = 30000; // 失败兜底快照的有效期
const ITEM_DETAIL_RETRY_DELAY = 600; // 命中频控后的重试间隔
const ITEM_DETAIL_CACHE_MAX = 20;
const itemDetailCache = {}; // key -> { time, res } 短窗口复用
const itemDetailPending = {}; // key -> Promise<axios response> 在途请求
const itemDetailFallback = {}; // key -> { time, res } 最近一次成功结果，仅失败时使用

// 返回响应副本，避免调用方（如把 items 的 buy 字段置 0）修改缓存中的结果
function copyItemDetailRes(res) {
    if (!res) return res;
    let data = res.data;
    try {
        data = JSON.parse(JSON.stringify(res.data));
    } catch (e) {
        data = res.data;
    }
    return Object.assign({}, res, { data });
}

// 取兜底快照；带 __fromFallback 标记，调用方可据此判断数据来自兜底
function getItemDetailFallback(key) {
    const item = itemDetailFallback[key];
    if (!item || Date.now() - item.time >= ITEM_DETAIL_FALLBACK_TTL) return null;
    return Object.assign(copyItemDetailRes(item.res), { __fromFallback: true });
}

function isRateLimited(res) {
    return !!(res && res.data && Number(res.data.sta) === 700);
}

// 频控时：有兜底快照就直接返回（交给外层兜底，不再补请求，减少服务端压力）；
// 没有兜底才延迟重试一次，尽量让首次进入的用户能拿到数据。
function fetchItemDetail(url, params, key) {
    return HttpHelper.get(url, { params }).then((res) => {
        if (!isRateLimited(res)) return res;
        if (getItemDetailFallback(key)) return res;
        return new Promise((resolve) => {
            setTimeout(() => {
                resolve(HttpHelper.get(url, { params }));
            }, ITEM_DETAIL_RETRY_DELAY);
        });
    });
}

function trimItemDetailCache(map) {
    const keys = Object.keys(map);
    if (keys.length <= ITEM_DETAIL_CACHE_MAX) return;
    keys.sort((a, b) => map[a].time - map[b].time);
    keys.slice(0, keys.length - ITEM_DETAIL_CACHE_MAX).forEach((key) => {
        delete map[key];
    });
}

export default class HomeService extends BaseServers {

    static async getEnrollDetail(type) {
    	let detail_url = "";
    	if(Config.TOKEN){
            detail_url = Config.api_detail;               		
    	}else{
    		detail_url = Config.api_noLogin_detail; //未登录的活动详情接口
    	}
    	
        return HttpHelper.get(detail_url, {
            params:
                {
                    eid: Config.EID,
                    access_token: Config.TOKEN,
                    referer:Config.referer,
                    spider: 'h5',
                }
        });
    }

    // 详情填写字段接口
    static async getReqDetail() {         	
        return HttpHelper.get(Config.api_req_detail, {
            params:
                {
                    eid: Config.EID,
                    access_token: Config.TOKEN,                    
                }
        });
    }

    //活动详情中的多项目数据
	static async getItemDetail(data, temp){
        let params =  {
            eid: Config.EID,                   
            access_token: Config.TOKEN
        }
        if(data){
            params.reserve_month = data.reserve_month
            params.item_key = data.item_key
        }
        let url = Config.api_item_detail 
        if(temp == 10){ //选座用V1接口
            url = Config.api_seat_detail
        }
        // 同一接口 + 同一活动 + 同一项目 + 同一月份 + 同一登录态，视为同一次请求
        const cacheKey = [
            url,
            params.eid || '',
            params.access_token || '',
            params.item_key || '',
            params.reserve_month || ''
        ].join('|');
        const cached = itemDetailCache[cacheKey];
        if (cached && Date.now() - cached.time < ITEM_DETAIL_CACHE_TTL) {
            return copyItemDetailRes(cached.res);
        }
        if (itemDetailPending[cacheKey]) { // 上一个相同请求还在路上，共用结果
            return itemDetailPending[cacheKey].then((res) => copyItemDetailRes(res));
        }
        const request = fetchItemDetail(url, params, cacheKey).then((res) => {
            delete itemDetailPending[cacheKey];
            // 只缓存成功结果，频控/异常结果不缓存，避免后续重试拿到错误数据
            if (res && res.data && Number(res.data.sta) === 0) {
                const snapshot = copyItemDetailRes(res);
                itemDetailCache[cacheKey] = { time: Date.now(), res: snapshot };
                itemDetailFallback[cacheKey] = { time: Date.now(), res: snapshot };
                trimItemDetailCache(itemDetailCache);
                trimItemDetailCache(itemDetailFallback);
                return res;
            }
            // 频控/网络/业务失败：用最近一次成功结果兜底，避免表单页项目列表被清空
            const fallback = getItemDetailFallback(cacheKey);
            return fallback || res;
        }, (err) => {
            delete itemDetailPending[cacheKey];
            const fallback = getItemDetailFallback(cacheKey);
            if (fallback) return fallback;
            return Promise.reject(err);
        });
        itemDetailPending[cacheKey] = request;
        return request.then((res) => copyItemDetailRes(res));
    }
    
    static async getEnrollUserList(lt) {
        return HttpHelper.get(Config.api_user_list, {
            params:
                {
                    eid: Config.EID,
                    info_id: lt,
                    count: Config.pageSize,
                    access_token: Config.TOKEN
                }
        });
    }

    static async getTeamList(team) {
        return HttpHelper.get(Config.team_list, {
            params:
                {
                    eid: Config.EID,
                    page: Config.pageCount,
                    count: Config.pageSize,
                    team: team,
                    access_token: Config.TOKEN || ""
                }
        });
    }

	//获取拼团结果详情
	static async getTeamDetail(tid) {
        return HttpHelper.get(Config.team_result_detail, {
            params:
                {
                    tid: tid,
                    access_token: Config.TOKEN
                }
        });
    }

    static async getMyEnroll(page,count) {
        return HttpHelper.get(Config.api_my_enroll, {
            params:
                {
                    eid: Config.EID,
                    access_token: Config.TOKEN,
                    page,
                    count,
                }
        });
    }

    static async getEnrollUserListByInfoId(info_id) {
        return HttpHelper.get(Config.api_user_detail, {
            params:
                {
                    eid: Config.EID,
                    info_id: info_id,
                    access_token: Config.TOKEN
                }
        });
    }

    static async saveEnrollInfo(info_id, info,items) {
        return HttpHelper.post(Config.api_update_enroll,
            {
                info_id: info_id,
                info: info,
                access_token: Config.TOKEN,
                items:items
            }
        );
    }

    static async exitEnroll(sub_mchid,info_id,reason,apply_reason) {
		let parm = {
			info_id: info_id,
			access_token: Config.TOKEN,
			role:0,
		}
		if(reason){
			parm.reason = reason;
            parm.apply_reason = apply_reason
		}
		
		return HttpHelper.post(Config.api_exit_enroll_merch,parm)
    }
	
	static async refundExitEnroll(info_id) {
		let parm = {
			info_id: info_id,
			access_token: Config.TOKEN,
		}
		return HttpHelper.post(Config.api_refun_exit,parm);
	}

    // 锁单待支付查询（GET，info_id）
    static async enrollLockQuery(info_id) {
        return HttpHelper.get(Config.api_enroll_lock_query, { params: { info_id, access_token: Config.TOKEN } });
    }

    // 创建锁单支付下单（POST，from='h5' / pay_source='h5'）
    static async enrollLockOrder(info_id) {
        const parm = {
            info_id,
            access_token: Config.TOKEN,
            from: 'enroll_h5_new',
            pay_source: 'h5'
        };
        // 非微信或非移动端使用网页支付。
        if(!WxApi.isMobile() || !WxApi.isWeixn()) {
            parm.pay_source = 'pc';
        }
        return HttpHelper.post(Config.api_enroll_lock_order, parm);
    }

    // 取消待支付锁单，释放活动名额
    static async enrollLockCancel(info_id) {
        return HttpHelper.post(Config.api_enroll_lock_cancel, {
            info_id,
            access_token: Config.TOKEN
        });
    }

    // 会员卡锁定待支付：按 is_renew 选择购买/续费路由
    static vipLockUrl(is_renew, action) {
        const buy = {
            query: Config.api_vip_lock_query,
            order: Config.api_vip_lock_order,
            cancel: Config.api_vip_lock_cancel,
            list: Config.api_vip_lock_list
        };
        const renew = {
            query: Config.api_vip_renew_lock_query,
            order: Config.api_vip_renew_lock_order,
            cancel: Config.api_vip_renew_lock_cancel,
            list: Config.api_vip_lock_list
        };
        return (is_renew ? renew : buy)[action];
    }

    // 查询会员卡锁定记录（exist=false 表示已支付/已取消/已超时回收）
    static async vipLockQuery(params) {
        return HttpHelper.get(this.vipLockUrl(params.is_renew, 'query'), {
            params: {
                lock_id: params.lock_id,
                access_token: Config.TOKEN
            }
        });
    }

    // 会员卡锁定下单（返回支付参数；h5 必须传 from=enroll_h5_new）
    // 会员卡锁定下单（返回支付参数；h5 必须传 from=enroll_h5_new）
    // pc / 非微信端传 pay_source=pc 才会返回 pay_qrcode（扫码支付），此时 package 为空、不可调 requestPayment
    static async vipLockOrder(params) {
        const parm = {
            lock_id: params.lock_id,
            access_token: Config.TOKEN,
            from: 'enroll_h5_new'
        };
        if (!WxApi.isMobile() || !WxApi.isWeixn()) {
            // 文档：pay_source=pc 返回二维码；旧字段名 source 仍兼容，两个都带保险
            parm.pay_source = 'pc';
            parm.source = 'pc';
        }
        return HttpHelper.post(this.vipLockUrl(params.is_renew, 'order'), parm);
    }

    // 取消会员卡锁定（放弃待支付，释放占位）
    static async vipLockCancel(params) {
        return HttpHelper.post(this.vipLockUrl(params.is_renew, 'cancel'), {
            lock_id: params.lock_id,
            access_token: Config.TOKEN
        });
    }

    // 会员卡待支付列表（页面刷新丢失 lock_id 时恢复用）
    static async vipLockList(params) {
        return HttpHelper.get(this.vipLockUrl(params.is_renew, 'list'), {
            params: {
                page: params.page || 1,
                count: params.count || 20,
                access_token: Config.TOKEN
            }
        });
    }

    static async submitEnroll(info, on_behalf, item, vip_id) {
        let encryptor = new JSEncrypt()  // 创建加密对象实例						
        const pubKey = `-----BEGIN PUBLIC KEY-----
            MIGfMA0GCSqGSIb3DQEBAQUAA4GNADCBiQKBgQCjI8E8LT0fwFekelMMuTWuaIfo
            fK69lyNIo+Vz0CGdfE3rLSIH94S2A3Q+bg+9/VnImvfXzcDVmqwHwC4hHPHs6hc6
            ufq0gfivTPms3kwX74F5qLMr70j4iZLt/PCkU+uyQ56KmRW4foCV4RPX8o8QZVss
            6eifHaeUeJxKM556ewIDAQAB
            -----END PUBLIC KEY-----`
        encryptor.setPublicKey(pubKey)//设置公钥   
        return HttpHelper.post(Config.api_enroll,
            {
                eid: Config.EID,
                info: info,
                on_behalf: on_behalf ? 1 : 0,
                items: item,
                access_token: Config.TOKEN,
                referer:Config.referer,
                from:'h5',
                vip_role: vip_id||'no',
                _a: encryptor.encrypt(Config.EID+Math.round(new Date().getTime()/1000)),
            }
        );
    }

    static async submitEnrollForPay(info, on_behalf, item,tid,sub_mchid,coupon_info_id,vip_id) {
        if(info && Array.isArray(info)){
            info = info.map(item=>{ //避免逻辑设置时，填写了数据，但是后面又修改了选项，结果数据还是提交了
                if(item.ignore == 1){
                    if(item.field_value) item.field_value = '';
                    if(item.new_field_value)item.new_field_value = '';               
                }
                return item;
            })
        }
       
    	let parm = {
                eid: Config.EID,
                info: info,
                on_behalf: on_behalf ? 1 : 0,
                items: item,
                access_token: Config.TOKEN,
                referer:Config.referer,
                vip_role: vip_id||'no',
            }

        if(!WxApi.isMobile() || !WxApi.isWeixn()){ //若为pc端，则调网页支付
            parm.pay_source = 'pc'
        }
  
        if(tid || tid==""){ //1、tid为空，代表拼团，2、tid有值，参团，3、无tid,单独购买
    		parm.tid = tid
    	}
        if(coupon_info_id){
            parm.coupon_info_id = coupon_info_id
        }
       
		if(sub_mchid){			
            parm.from = 'enroll_h5_new';
			return HttpHelper.post(Config.api_enroll_merch,parm);
		}else{
            let encryptor = new JSEncrypt()  // 创建加密对象实例						
            const pubKey = `-----BEGIN PUBLIC KEY-----
                MIGfMA0GCSqGSIb3DQEBAQUAA4GNADCBiQKBgQCjI8E8LT0fwFekelMMuTWuaIfo
                fK69lyNIo+Vz0CGdfE3rLSIH94S2A3Q+bg+9/VnImvfXzcDVmqwHwC4hHPHs6hc6
                ufq0gfivTPms3kwX74F5qLMr70j4iZLt/PCkU+uyQ56KmRW4foCV4RPX8o8QZVss
                6eifHaeUeJxKM556ewIDAQAB
                -----END PUBLIC KEY-----`
            encryptor.setPublicKey(pubKey);//设置公钥   
            parm._a = encryptor.encrypt(Config.EID+Math.round(new Date().getTime()/1000));
            parm.from = 'h5';
			return HttpHelper.post(Config.api_enroll,parm);
		}
        
    }
    
    //绑定商户支付分两步走，通过temp_id请求支付参数
    static async submitBusinessPay(tmp_id) {
        return HttpHelper.post(Config.api_enroll_pay,
            {
                access_token: Config.TOKEN,
                tmp_id:tmp_id,
            }
        );
    }

    static async getEnrollCert(info_id) {
        return HttpHelper.get(Config.api_cert_detail, {
            params: {
                    info_id: info_id,
                    access_token: Config.TOKEN
                }
            }
        );
    }
	
	static async getEnrollCustom() {
	    return HttpHelper.get(Config.api_cert_custom, {
	        params: {
	                eid: Config.EID,
	                access_token: Config.TOKEN
	            }
	        }
	    );
	}

    static async uploadImage(media_id) {
    	
        return HttpHelper.post(Config.api_upload_file, {
				source: Config.bm_domain == true ? 'h5' : '',
                media_id: media_id,
                access_token: Config.TOKEN

            }
        );
    }

    static async uploadBase(base64) {
        return HttpHelper.post(Config.api_upload_file, {
                bs64: base64,
                access_token: Config.TOKEN

            }
        );
    }

    static async getShare(type = 0, extraParams = {}) {
        const params = {
            eid: Config.EID,
            type: type,
            img_source:'h5',
            ...extraParams,
        };
        // 未登录生成分享码时不发送空 token，避免接口误判为需要登录。
        if(Config.TOKEN){
            params.access_token = Config.TOKEN;
        }
        return HttpHelper.get(Config.api_get_share, { params });
    }

    static async getMiniShortLink() {
        const params = { eid: Config.EID };
        if(Config.TOKEN){
            params.access_token = Config.TOKEN;
        }
        return HttpHelper.get(Config.api_generate_shortlink, { params });
    }

    static async getHomePageShare(unionid) {
        return HttpHelper.get(Config.api_get_homepage_share, {
            params: {
                    unionid: unionid,
                }
            }
        );
    }

    // 获取主页二维码
    static async getQrCode(data) {
        return HttpHelper.get(Config.api_share_h5, {
            params: {
                    unionid: data.unionid,
                    type: data.type,
                    access_token: Config.TOKEN,
                }
            }
        );
    }

    static async subscribeHomepage(type, unionid) {
        return HttpHelper.post(Config.api_subscribe_homepage, {
            unionid: unionid,
            access_token: Config.TOKEN,
            switch: type,
        });
    }
    //收藏和取消收藏
    static async setCollect(type) {

        return HttpHelper.post(Config.api_collect_enroll, {
            eid: Config.EID,
            access_token: Config.TOKEN,
            fav:type? 0: 1,
        });
    }
    
    
    //提交举报信息
    static async submitReport(reason) {
    	
        return HttpHelper.post(Config.api_enroll_report, {
            eid: Config.EID,
            access_token: Config.TOKEN,
            reason:reason,
        });
    }
    
    
    //获取订阅消息场景值
    static async getSceneId() {
    	return HttpHelper.get(Config.api_scene_id, {
            params: {
                    eid: Config.EID,
            		access_token: Config.TOKEN
                }
            }
        );
    }
    
    //订阅消息
    static async subNotice(scene_id,openid) {
    	
    	 return HttpHelper.post(Config.api_scene_notice, {
            eid: Config.EID,
            access_token: Config.TOKEN,
            scene_id:scene_id,
            openid:openid,
        });
    }
    
     //口令验证
    static async subToken(token) {
    	 return HttpHelper.post(Config.api_enroll_token, {
            eid: Config.EID,
            access_token: Config.TOKEN,
            token:token,
        });
    }
    
	//获取留言列表
	static async getMessageList(type) {
	    return HttpHelper.get(Config.api_detail_message_list, {
	        params:
	            {
	                eid: Config.EID,
	                page: Config.pageCount,
	                count: Config.pageSize,
	                access_token: Config.TOKEN || "",
					type:type,
				}
			});
	}
	
	//添加留言
	static async addMessage(content,device,c_pic) {
	    return HttpHelper.post(Config.api_detail_message_list, {
				eid: Config.EID,
				access_token: Config.TOKEN || "",
				content,
                device,
                c_pic
			});
	}
	
	//删除留言
	static async deleteMessage(msg_id) {
	    return HttpHelper.delete(Config.api_detail_message_list, {
				data:{
					eid: Config.EID,
					access_token: Config.TOKEN || "",
					msg_id:msg_id,
				}
			});
	}
	// 回复留言
    static async replayMessage(params){
        return HttpHelper.post(Config.api_replay_message, {
            eid: Config.EID,
            access_token: Config.TOKEN || "",
            content:params.content,
            msg_id:params.msg_id,
            reply_id:params.reply_id,
            device:params.device,
            c_pic:params.c_pic,
        });        
    }
    // 置顶 or 公开留言
    static async setMsgControl(params){
        return HttpHelper.post(Config.api_msg_control, {
            eid: Config.EID,
            access_token: Config.TOKEN,
            msg_id:params.msg_id,
            fav:params.fav,
            top:params.top,
        });        
    } 
    // 绑定手机号    
    static async toBindPhone(params){
        return HttpHelper.post(Config.api_bind_phone, {           
            access_token: Config.TOKEN,
            phone:params.phone,
            code:params.code,            
        });     
    }
    // 解绑手机号
    static async toUnbindPhone(){
        return HttpHelper.post(Config.api_unbind_phone, {           
            access_token: Config.TOKEN,         
        });     
    }
    // 重置密码
    static async toResetPwd(params){
        return HttpHelper.post(Config.api_reset_pwd, {           
            access_token: Config.TOKEN,
            phone:params.phone,
            code:params.code,
            password:params.password,
            affirm_password:params.affirm_password,     
        });     
    }    
    // 换绑手机号
    static async toChangePhone(params){
        return HttpHelper.post(Config.api_change_phone, {           
            access_token: Config.TOKEN,
            phone:params.phone,
            code:params.code,
            old_password:params.old_password,
            source:'h5'
        });     
    }    
	static async getUserInfo() {
	    return HttpHelper.get(Config.api_get_userInfo, {
	        params:
	            {
	                access_token: Config.TOKEN,
	            }
	    });
	}
	
	static async getAuthInfo(options = {}) {
		let url = Config.api_get_authInfo;
        let Renew = sessionStorage.getItem('Renew')
        let Auth_status = sessionStorage.getItem('Auth_status')
		if(Renew && (Auth_status == 2)){
			url = Config.api_get_renewInfo;
		}
		
	    return HttpHelper.get(url, {
	        params:
	            {
	                access_token: Config.TOKEN,
	                source: 'h5',
	                task: options.task,
	            }
	    });
	}

    //暂存个人信息
	static async subAuthLog(info) {
		let parm = null;
        parm = info;
        parm.access_token = Config.TOKEN;
        parm.commit = 1;

		let isMobile = WxApi.isMobile();
		if(!isMobile){
			parm.source = 'pc';
		}

		let url = Config.api_auth_log;
		return HttpHelper.post(url, parm);
	}
	
	//保存企业组织公众号信息
	static async subAuthInfo(info,payType) {
		let parm = null;
		if(info.fromP){
			parm = {
				access_token: Config.TOKEN,
				full_name:info.full_name,
				legal_person:info.legal_person,
				license_no:info.license_no,
				license_pic:info.license_pic,
				type:info.type,
			}
		}else{
			 parm = info;
			 parm.access_token = Config.TOKEN;
			 parm.commit = 1;
		}
 		
		let isMobile = WxApi.isMobile();
		if(!isMobile){
			parm.source = 'pc';
		}
        
        if(!WxApi.isMobile() || !WxApi.isWeixn()){ //若为pc端，则调网页支付
            parm.pay_source = 'pc';
        }  
        
        if(!WxApi.isWeixn()){ //pc上打开才加此参数
            parm.from = 'enroll_h5_new';
        }

        if(payType == 'other'){ //代付  
            let url = Config.api_apply_replace;
            let Renew = sessionStorage.getItem('Renew');
            let Auth_status = sessionStorage.getItem('Auth_status');
            if(Renew && Auth_status == 2){
                url = Config.api_apply_renew_replace;
            }
            return HttpHelper.post(url, parm);
        }else{
            let url = Config.api_set_companyInfo;
            let Renew = sessionStorage.getItem('Renew')
            let Auth_status = sessionStorage.getItem('Auth_status')
            if(Renew && Auth_status == 2){
                url = Config.api_set_renewInfo;
            }
            return HttpHelper.post(url, parm);
        }
		
	}
	
	static async getCode(phone) {
	    return HttpHelper.post(Config.api_get_code, {
			access_token: Config.TOKEN,
			phone:phone,
	    });
	}
    // 免登录获取验证码
    static async getPwdCode(phone) {
	    return HttpHelper.post(Config.api_pwd_code, {		 
			phone:phone,
	    });
	}	
	//公众号VIP验证码校验
	static async validateCode(code) {
		let url = Config.api_validate_code;
        let Renew = sessionStorage.getItem('Renew')
        let Auth_status = sessionStorage.getItem('Auth_status')
		if(Renew && Auth_status== 2){
			url = Config.api_validate_renewCode;
		}
	    return HttpHelper.post(url, {
			access_token: Config.TOKEN,
			code:code,
	    });
	}
	
	static async getPcPayResult(orderNo) {
	    return HttpHelper.get(Config.api_get_pc_payResult, {
	        params:
	            {
	                order_no: orderNo,
	                access_token: Config.TOKEN
	            }
	    });
	}
	
	//获取座位图信息
	static async getSeatInfo(skey) {
	    return HttpHelper.get(Config.api_seat_info, {
	        params:
	            {
	                eid: Config.EID,
	                access_token: Config.TOKEN,
					key: skey
	            }
	    });
	}

    //最近浏览列表
    static async getEnrollHistory(){
        return HttpHelper.get(Config.api_user_history,{
            params:
            {
                access_token: Config.TOKEN,
            }
        })
    }

    // 我管理的&我参与的
    static async getManageList(data){
        return HttpHelper.get(Config.api_manage_list,{
            params:
            {
                access_token: Config.TOKEN,
                type:data.type,
                page:data.page,
                count:data.count,
                cancel_status:data.cancel_status,
                keyword:data.keyword,
                time_out:data.time_out,
                status:data.status,
                is_create:data.is_create,
            }
        })
    }

    // 公开活动
    static async toPublicEnroll(data){
        return HttpHelper.post(Config.api_enroll_public,{
            access_token: Config.TOKEN,
            type:data.type,
            send_msg:data.send_msg,
            eid:data.eid
        })
    }

    // 个人主页详情
    static async getHomePageData(unionid){
        return HttpHelper.get(Config.api_homepage_info,{
          params:{
            access_token: Config.TOKEN,
            unionid,
          }
        })
    }

    // 个人主页详情（免登录）
    static async getHomePageV2(unionid){
        return HttpHelper.get(Config.api_homepage_info2,{
          params:{
            access_token: Config.TOKEN,
            unionid,
          }
        })
    }

    // 个人主页推荐列表
    static async recommendHomePage(){
        return HttpHelper.get(Config.api_common_homepage)
    }
    
    // 公开活动列表
    static async getEnrollList(data){
        return HttpHelper.get(Config.api_web_enroll,{
          params:{
            access_token: Config.TOKEN,
            page:data.page,
            count:data.count,
            unionid:data.unionid,
            keyword:data.keyword,
            type:data.type,
            home_type: data.home_type,
          }
        })
    }
    
	//增加消息订阅次数  type 1 获取数据  2增加订阅次数
    static async addSubNotice(type){ 
        const data = ['3VnoJn7ILmAeFH3wxCZyktTIbRNogLt_8ntfmFG5Jl0','l9GmcM9xfZOMWoorpD24LpiGr_oQf6ZGxdN08ooEWR0','E70ynK2JfAiMoRgGExzPfju2VGAXv9uwH-vWYa16yyg']
        let params = {
            access_token: Config.TOKEN,
        }
        if(type == 2){ //2增加订阅次数
            params.template_ids = data
            return HttpHelper.post(Config.api_sub_notice,params)
        }else{ //1是获取数据
            return HttpHelper.get(Config.api_sub_notice,{params})
        }
    }

    // 我关注的列表
    static async getSubList(data){
        return HttpHelper.get(Config.api_sub_list,{
          params:{
            access_token: Config.TOKEN,
            page:data.page,
            count:data.count
          }
        })
    }

    // 删除快速填写信息
    static async deleteExtraInfo(params){
        return HttpHelper.delete(Config.api_extra_info,{
            data:{
                access_token: Config.TOKEN,
                key:params.key,
                old_key:params.old_key,
            }
        })
    }

    // 新增 快速填写信息
    static async saveExtraInfo(params){
        return HttpHelper.post(Config.api_extra_info,{
            access_token: Config.TOKEN,
            key:params.key,
            old_key:params.old_key,
            name:params.name,
            value:params.value
        })
    }

    // 获取扫码登录的二维码
    static async getLoginCode(){
        return HttpHelper.get(Config.api_login_code)
    } 
    
    // 扫码登录
    static async scanCodeLogin(code){
        return HttpHelper.get(Config.api_pc_login,{
            params:{
                code
            }
        })
    }

    // 消息提醒
    static async switchNotice(params,type='send'){
        if(type == 'send'){
            return HttpHelper.post(Config.api_notice_switch,{
                access_token: Config.TOKEN,
               ...params
            })
        }else{
            return HttpHelper.get(Config.api_notice_switch,{
                params:{
                    access_token: Config.TOKEN,
                }
            })
        }
       
    }

    // 会员卡详情1
    static async vipCardDetail(id){
        return HttpHelper.get(Config.api_vip_card_detail,{
            params:{
                access_token:Config.TOKEN,
                card_id:id
            }
        })
    }

    // 会员卡列表 （从报名中申请）
    static async vipCardList(unionid){
        return HttpHelper.get(Config.api_card_list,{
            params:{
                access_token:Config.TOKEN,
                target_unionid:unionid
            }
        })
    }

    // 购买会员卡
    static async payVipCard(params){
        const data = {
            access_token:Config.TOKEN,
            card_id:params.card_id,
            phone:params.phone,
            user_name:params.user_name,
            union_key:params.union_key,
            code:params.code,
            id_card:params.id_card,
            address:params.address,
            sex:params.sex,
            day_type:params.day_type,
            birthday:params.birthday,
            cert_num: params.cert_num,
            from:'enroll_h5_new',           
        }        
        if(!WxApi.isMobile()){//非微信端，则加此参数，返回微信支付二维码
            data.source = 'pc'
        }       
        return HttpHelper.post(Config.api_vip_card_pay,data)
    }

    // 会员卡续费
    static async toRenewVip(params){   
        const data = {
            access_token:Config.TOKEN, 
            phone:params.phone,
            user_name:params.user_name,
            wx_no:params.wx_no,
            card_id:params.card_id, 
            vip_id:params.vip_id,
            code:params.code,
            id_card:params.id_card,
            address:params.address,
            sex:params.sex,
            day_type:params.day_type,
            birthday:params.birthday,
            cert_num: params.cert_num,
            from:'enroll_h5_new'                   
        }

        if(!WxApi.isMobile()){//非微信端，则加此参数，返回微信支付二维码
            data.source = 'pc'
        }       
        return HttpHelper.post(Config.api_vip_renew_pay,data)
    }

    // 查看会员详情
    static async getVipUserDetail(out_trade_no,vip_id){
        let  params = {
            access_token:Config.TOKEN,           
        }
        if(out_trade_no){
            params.out_trade_no = out_trade_no
        }
        if(vip_id){
            params.vip_id = vip_id
        }
        return HttpHelper.get(Config.api_user_vip_detail,{
            params
        })
    }

    // 会员卡购买记录
    static async getVipList(data){      
        return HttpHelper.get(Config.api_vip_order_list,{
           params:{
                access_token:Config.TOKEN, 
                count:data.count,
                vip_unionid:data.vip_unionid,
                card_id:data.card_id,
                page:data.page,     
                type: 'my'         
           }
        })
    }

    // 清除最近浏览记录
    static async delHistory(){
        return HttpHelper.post(Config.api_history_del,{
            access_token:Config.TOKEN,
        })
    }
    
    // 查看是否开启企业微信提醒
    static async getWorkConfig(){
        return HttpHelper.get(Config.api_work_config,{
            params:{
                access_token:Config.TOKEN,
            }
        })
    }

    // 注销账号
    static async toCancelUser(){
        return HttpHelper.post(Config.api_cancel_user,{
            access_token:Config.TOKEN,
        })
    }

    // h5获取ticket
    static async getShortTicket(data){
        return HttpHelper.get(Config.api_short_ticket,{
            params:{
                eid: data.eid,
                referer: data.referer,
            }
        })
    }

    // 获取用户信息
    static async getAuthMsg(id){
        return  HttpHelper.get(Config.api_get_authInfo,{
            params:{
                unionid:id,
                access_token:Config.TOKEN
            }
        })
    }

    // 手机号登录
    static async toPhoneLogin(params){
        return HttpHelper.post(Config.api_login_phone,{
            password:params.password,
            phone:params.phone,
            encrypt:1,
            from:"h5"
        })
    }

    //退款记录    
    static async getRefundList(info_id){
        return HttpHelper.get(Config.api_list_info,{
           params:{
                access_token:Config.TOKEN,
                info_id
           }
        })
    }

    // 退款订单详情
    static async getRefundDetail(data){
        return HttpHelper.get(Config.refund_detail,{
            params:{
                access_token:Config.TOKEN,
                info_id:data.info_id,
                out_refund_no:data.out_refund_no
            }
        })
    }
    
    //获取上次提交记录    
    static async getPeriodDetail(){
        return HttpHelper.get(Config.api_period_detail,{
            params:{
                access_token:Config.TOKEN,
                eid:Config.EID             
            }
        })
    }

    // 编辑个人信息
    static async editUserMessage(data,type){
        if(type == 'get'){
            return HttpHelper.get(Config.api_user_edit,{
                params:{
                    access_token:Config.TOKEN,                  
                }
            })
        }else{
            return HttpHelper.post(Config.api_user_edit,{
                access_token:Config.TOKEN,
                uname:data.uname,
                upic:data.upic,
                user_sex: data.user_sex,
                account_phone:data.account_phone,
                code:data.code
            })
        }
       
    }

    // 团购订单
    static async getCartOrder(data){
        return HttpHelper.get(Config.api_cart_orders,{
            params:{
                access_token:Config.TOKEN, 
                page: data.page,
                eid: Config.EID,
                count: data.count              
            }
        })
    }
    
    // 收款码付款    
    static async toCodePay(params){
        const data = {
            access_token: Config.TOKEN, 
            union_key: params.union_key,
            unionid: params.unionid,
            fee: params.fee,
            remark: params.remark,
            from:'enroll_h5_new',            
        }
        if(params.source){
            data.source = params.source;
        }
        if(params.eid){
            data.eid = params.eid;
        }
        if(params.referer){ //邀请人
            data.referer = params.referer;
        }
        return HttpHelper.post(Config.api_code_pay,data)
    }

    // 收款商户信息
    static async getCashierDetail(unionid,type,eid){
        const params = {
            access_token: Config.TOKEN,            
            unionid,          
        }
        if(type == 'reward'){
            params.source = 'reward'
            params.eid = eid
        }
        return HttpHelper.get(Config.api_cashier_detail,{
            params,
        })
    }

    // 未登录情况下查看商户收款码
    static getMerchantDetail(unionid){        
        return HttpHelper.get(Config.api_cashier_detail,{
            params: {unionid, source: 'h5'},
        })
    }

    // 我的优惠券列表
    static async getCouponList(data){
        return HttpHelper.get(Config.api_coupon_list,{
            params:{
                access_token: Config.TOKEN,                            
                page:data.page,
                count:data.count,
                coupon_unionid:data.coupon_unionid,   
            }
        })
    }

    // 代付链接详情   
    static async getReplaceInfo(data){
        return HttpHelper.get(Config.api_apply_replace_info,{
            params:{
                out_trade_no:data.out_trade_no,
                type:data.type
            }
        })
    }

    // 代付拉起支付
    static async applyOtherPay(out_trade_no){
        let parm = {
            access_token: Config.TOKEN, 
            out_trade_no: out_trade_no,
            from:'enroll_h5_new',            
        }
        if(!WxApi.isMobile() || !WxApi.isWeixn()){ //若为pc端，则调网页支付
            parm.pay_source = 'pc'
        }
        return HttpHelper.post(Config.api_apply_replace_pay,parm)
    }

    // 删除留言
    static async toDeleteMsg(msg_id,reply_id){
        return HttpHelper.post(Config.api_msg_del,{
            access_token: Config.TOKEN,
            eid:Config.EID,
            msg_id,
            reply_id
        })
    }

    // 查询用户是否能预约
    static searchMyEnroll(){
        return HttpHelper.get(Config.api_my_enroll,{
            params:{
                access_token: Config.TOKEN,
                user_joins:'all',
                page:1,
                count:10,
                eid:Config.EID
            }
        })
    }   
    
    // 生成分享SchemeUrl
    static getSchemeUrl(params){
        return HttpHelper.post(Config.api_scheme_url,{
            path:params.path,
            expire_time:params.expire_time,
            scene_id:params.scene_id
        })
    }    

    // 验证会员数据
    static getVerifyVip(vip_id){
        return HttpHelper.get(Config.api_verify_vip,{
            params:{
                access_token: Config.TOKEN,
                vip_id
            }
        })
    }
    
    // 提交确认会员数据
    static toVerifyVip(params){
        return HttpHelper.post(Config.api_verify_vip,{
            access_token: Config.TOKEN,
            vip_id:params.vip_id,
            phone:params.phone,
            code:params.code
        })
    }    

    // 我加入的会员列表
    static getMyVipCard(){
        return HttpHelper.get(Config.api_my_join_vip,{
            params:{
                access_token: Config.TOKEN,
                page:1,
                count:100,
            }                  
        })
    }
    // 转让参与记录
    static toMoveInfo(info,info_id){
        return HttpHelper.post(Config.api_move_info,{
            access_token: Config.TOKEN,
            info_id,
            eid:Config.EID,
            info,
        })
    }
    // 用户标记数据分享
    static toShareInfo(data){
        const params = {
            access_token: Config.TOKEN,
            info_id: data.info_id,          
        }
        if(data.share_type){
            params.share_type = data.share_type;
        }
        if(data.move_reason){
            params.move_reason = data.move_reason;
        }
        if(data.phone){
            params.phone = data.phone;
        }
        return HttpHelper.post(Config.api_info_share, params)
    }
    
    // 钉钉消息配置
    static getDingConfig(){
        return HttpHelper.get(Config.api_ding_webhook,{
            params:{
                access_token: Config.TOKEN              
            }                  
        })
    }
    // 获取编辑历史记录
    static getEditHistory(data){
        return HttpHelper.get(Config.api_edit_list,{
            params:{
                access_token: Config.TOKEN,
                info_id: data.info_id,   
                page: data.page,
                count: data.count,
            }                  
        })
    }

    // 免登录查看付款价格配置
    static getPriceList(unionid){
        return HttpHelper.get(Config.api_price_list,{
            params:{
                unionid
            }                  
        })
    }

    // 回收站列表
    static getBackList(data){        
        return HttpHelper.get(Config.api_bak_list,{
            params:{
                access_token: Config.TOKEN,
                page: data.page,
                count: data.count,
                keyword: data.keyword,
            }                  
        })
    }
    // 恢复活动
    static toRecoverEnroll(eid){
        return HttpHelper.post(Config.api_recover_enroll,{
            access_token: Config.TOKEN,
            eid,          
        })
    }
    // 彻底删除活动
    static toDeleteEnroll(eid){
        return HttpHelper.post(Config.api_direct_del,{
            access_token: Config.TOKEN,
            eid,          
        })
    }
    // 授权code，获取openid
    static getOpenid(data){
        return HttpHelper.get(Config.api_ext_openid,{
            params:{
                app_id: data.appid,             
                code: data.code,
            }                  
        })        
    }
    // 测试下单
    static toPayTest(data){   
        return HttpHelper.post(Config.api_pay_test,{
            app_id: data.appid,                         
            openid:data.openid,
            pay_type:data.pay_type,                            
        })        
    }    
    // 注册签名(测试支付，无token)
    static toPaySign(data){   
        return HttpHelper.post(Config.api_pay_sign,{             
            app_id:data.appid,                         
            url:data.url,                                
        })        
    }
    // 核销签到
    static toCheckCert(data){           
        if(data.eid){
            return HttpHelper.get(Config.api_cert_check,{ //查看项目报名签到信息          
                params:{
                    access_token: Config.TOKEN,                        
                    eid:data.eid,   
                }                             
            })    
        }else{
            const params = { //去签到         
                access_token: Config.TOKEN,                        
                info_id:data.info_id                          
            }
            if(data.items && data.items.length){ //多个预约时间单独签到
                params.items = data.items;
            }
            return HttpHelper.post(Config.api_cert_check,params)  
        }           
    }

    // 查询扫码核销凭证详情
    static getCertVerify(data){
        return HttpHelper.get(Config.api_cert_verify, {
            params: {
                access_token: Config.TOKEN,
                info_id: data.info_id,
            }
        })
    }

    // 按递增编号查询扫码核销凭证详情
    // 注意 source 必须与小程序一致传 'xcx'：后端可能按 source 分支返回不同字段结构（传 h5 时返回数据不完整）
    static getCertUserDetail(data){
        return HttpHelper.get(Config.api_cert_user_detail, {
            params: {
                access_token: Config.TOKEN,
                eid: data.eid,
                rank_no: data.rankNo,
                source: 'xcx',
            }
        })
    }

    // 编号核销提交（与小程序 toRankVerify 对齐：eid + rank_no + cert_type=3）
    static rankNoVerify(data){
        const params = {
            access_token: Config.TOKEN,
            eid: data.eid,
            rank_no: data.rank_no,
            cert_type: 3,
        };
        if (data.items && data.items.length) {
            params.items = data.items;
        }
        return HttpHelper.post(Config.api_cert_rank_no_verify, params);
    }

    // 撤销单条核销记录
    static cancelCertInfo(certId){
        return HttpHelper.post(Config.api_cancel_cert_info, {
            access_token: Config.TOKEN,
            c_id: certId,
        })
    }

    // 扫码核销提交（与小程序 /enroll/v1/cert/cancel 对齐，支持 items 参数）
    static submitCertVerify(data){
        const params = {
            access_token: Config.TOKEN,
            info_id: data.info_id,
            cert_type: 1,  // 固定为 1：扫码核销（小程序根据 _scene 判断，H5 一律走扫码）
        };
        if (data.items && data.items.length) {
            params.items = data.items;
        }
        return HttpHelper.post(Config.api_cert_cancel, params);
    }

    // 核销签到：凭证配置（有效期/签到地点），与小程序 getCertConfig 对齐
    static getCertConfig(){
        return HttpHelper.get(Config.api_cert_config, {
            params: {
                access_token: Config.TOKEN,
                eid: Config.EID,
            }
        })
    }

    // 核销签到：核销/待核销数量与自助签到二维码（与小程序 loadImage 对齐）
    // url_type 0=圆形码(/cert/qrcode)，1=方形码(/cert/h5_cancel_img，认证用户可用)
    static getCertQrcode(data){
        const params = {
            access_token: Config.TOKEN,
            eid: Config.EID,
            img_size: data.img_size || 500,
            url_type: data.url_type || 0,
        };
        if (data.code_sn_id) {
            params.code_sn_id = data.code_sn_id;
        }
        let url = Config.api_cert_qrcode;
        if (params.url_type == 1) {
            url = Config.api_cert_h5_cancel_img;
            params.type = 'enroll';
        }
        return HttpHelper.get(url, { params });
    }

    // 核销签到：自助核销码（副码）列表，与小程序 getCertCodeList 对齐
    static getCertCodeList(){
        return HttpHelper.get(Config.api_cert_qrcode_list, {
            params: {
                access_token: Config.TOKEN,
                eid: Config.EID,
                page: 1,
                count: 10,
            }
        })
    }

    // 核销签到：添加/编辑自助核销码副码，与小程序 addCertCode 对齐
    static saveCertCode(data){
        const params = {
            access_token: Config.TOKEN,
            title: data.title,
            type: data.type || 'add',
            eid: Config.EID,
        };
        if (data.type === 'edit' && data.qrcode_id) {
            params.qrcode_id = data.qrcode_id;
        }
        return HttpHelper.post(Config.api_cert_save_qrcode, params);
    }

    // 报名查询列表（核销签到页"已核销/未核销"入口，与小程序 searchuser 的 user_list 参数对齐）
    static getCheckUserList(data){
        const params = {
            access_token: Config.TOKEN,
            eid: Config.EID,
            type: 0,
            query: data.query || '',
            count: 30,
            page: data.page || 1,
            verified: data.verified,
            v_type: 'v2',
        };
        if (data.time_type != null) params.time_type = data.time_type;
        if (data.item_key) params.item_key = data.item_key;
        if (data.start_time) params.start_time = data.start_time;
        if (data.end_time) params.end_time = data.end_time;
        if (data.order != null) params.order = data.order;
        if (data.no_refund != null) params.no_refund = data.no_refund;
        return HttpHelper.get(Config.api_user_list, { params });
    }

    // 验证手机号和验证码
    static checkPhone(data){
        const params = {
            access_token: Config.TOKEN,
            phone: data.phone,
            code: data.code,
        }
        return HttpHelper.post(Config.checkPhoneCode,params)  
    }
    // vip会员登录
    static vipToLogin(data){
        const params = {
            access_token: Config.TOKEN,
            unionid: data.unionid,
        }
        return HttpHelper.post(Config.vipLogin,params)  
    }
    // 订单投诉
    static orderComplaint(params,type){
        if(type == 'get'){
            return HttpHelper.get(Config.api_order_complaint,{
                params:{
                    access_token: Config.TOKEN,            
                    out_trade_no: params.out_trade_no,  
                }              
            }) 
        }else {
            return HttpHelper.post(Config.api_order_complaint,{
                access_token: Config.TOKEN,
                reason: params.reason,
                content: params.content,
                out_trade_no: params.out_trade_no,
                pics: params.pics,
                phone: params.phone
            }) 
        }        
    }
    // 撤销投诉详情
    static cancelComplaint(params){
        return HttpHelper.post(Config.api_complaint_cancel,{
            access_token: Config.TOKEN,
            complaint_id: params.complaint_id,
            grant: params.grant,           
        })  
    }
    // 获取所有的消息通知
    static getNoticeAll(eid){
        return HttpHelper.get(Config.api_notice_all,{
           params: {
            access_token: Config.TOKEN,
            eid
           }
        })
    }
    // 发送微信服务通知（对齐小程序 /enroll/v2/notice/send）
    static sendNotice(params){
        let data = {
            access_token: Config.TOKEN,
            eid: params.eid,
            content: params.content,
            notice_img: params.notice_img || '',
            file_info: params.file_info || null,
        };
        if(params.to_eid){
            data.to_eid = params.to_eid;
        }
        // 编辑修改通知：对齐小程序，改走 notice/edit 并带 msg_id
        if(params.msg_id){
            data.msg_id = params.msg_id;
            return HttpHelper.post(Config.api_notice_edit, data);
        }
        return HttpHelper.post(Config.api_notice_send, data);
    }
    // 通知设为过期（作废），对齐小程序 setExceed
    static noticeTimeout(msg_id, eid){
        return HttpHelper.post(Config.api_notice_timeout,{
            access_token: Config.TOKEN,
            msg_id,
            eid
        });
    }
    // 最近六个月参与记录
    static getJoinList(data){
        return HttpHelper.get(Config.api_join_list,{
            params: {
                access_token: Config.TOKEN,
                page: data.page,
                count: data.count,
                unionid: data.unionid,
                owner_unionid: data.owner_unionid,  
            }  
        }) 
    }

    // 转载活动
    static reshippedEnroll(params){
        return HttpHelper.post(Config.api_reshipped_enroll,{
            access_token: Config.TOKEN,
            type: params.type,
            eid: params.eid,           
        })  
    }

    // 邀请参团
    static toShareGroup(data){
        return HttpHelper.get(Config.api_group_share,{
            params: {
                access_token: Config.TOKEN,
                eid: Config.EID,
                tid: data.tid,
                type: data.type,      
            }  
         }) 
    }
    // 转让记录
    static toShareGroup(data){
        return HttpHelper.get(Config.api_group_share,{
            params: {
                access_token: Config.TOKEN,
                eid: Config.EID,
                tid: data.tid,
                type: data.type,      
            }  
         }) 
    }
    // 分享记录
    static getShareList(data){
        return HttpHelper.get(Config.api_share_list,{
            params: {
                access_token: Config.TOKEN,
                eid: Config.EID,
                count: data.count,
                page: data.page,      
            }  
        }) 
    }
    // 获取共创团队列表
    static getTeamUser(data){
        return HttpHelper.get(Config.api_team_user,{
            params: {
                access_token: Config.TOKEN,
                page: data.page,
                count: data.count,
                unionid: data.unionid,
                source: 'h5'
            }
        })
    }    
    // 分享口令
    static getSearchToken(){
        return HttpHelper.get(Config.api_search_token,{
            params: {
                access_token: Config.TOKEN,
                eid: Config.EID,
            }
        })
    }   
    // 发送投诉留言
    static sendComplaintMsg(params){
        return HttpHelper.post(Config.api_complaint_msg,{
            access_token: Config.TOKEN,
            out_trade_no: params.out_trade_no,
            content: params.content,
            pics: params.pics,
        })
    }
    // 投诉留言列表
    static getComplaintList(data){
        return HttpHelper.get(Config.api_complaint_list,{
            params: {
                access_token: Config.TOKEN,
                out_trade_no: data.out_trade_no,
                page: data.page,
                count: data.count,
            }
        })
    }

    // 订单详情
    static getOrderDetail(data){        
        return HttpHelper.get(Config.api_order_detail,{
            params: {
                access_token: Config.TOKEN,
                out_trade_no: data.out_trade_no,               
                eid: Config.EID,
                unionid: data.unionid     
            }
        }) 
    }
    // 商户信息详情
    static getMerchantMsg(){
        return HttpHelper.get(Config.api_merchant_apply, {
            params: {
                access_token: Config.TOKEN,
            }
        })
    }
    // 查询进行中活动的数量
    static getEnrollNum(){
        return HttpHelper.get(Config.api_enroll_num, {
            params: {
                access_token: Config.TOKEN,
            }
        })
    }
    // 一键截止所有活动
    static closeEnrollAll(){
        return HttpHelper.post(Config.api_close_enroll, {
            access_token: Config.TOKEN,
        })
    }
    // VIP权益转让
    static authReplace(unionid){
        return HttpHelper.post(Config.api_auth_replace, {
            access_token: Config.TOKEN,
            new_unionid: unionid,
        })
    }
    // 管理员记录
    static adminHistory(params = {}){
        return HttpHelper.get(Config.api_admin_history, {
            params: {
                eid: params.eid || Config.EID,
                target_unionid: params.target_unionid || '',
                access_token: Config.TOKEN,
            }
        })
    }

    // 管理员维护接口，保持与小程序 admin 页面使用的参数协议一致。
    static adminList(params = {}){
        return HttpHelper.get(Config.api_admin_list, {
            params: {
                eid: params.eid || Config.EID,
                access_token: Config.TOKEN,
            }
        });
    }

    // 当前用户在该活动的管理员信息（头像/昵称/添加时间/权限节点），
    // 参数协议与小程序 adminRole 页 getAdminInfo 一致：eid + access_token
    static adminInfo(params = {}){
        return HttpHelper.get(Config.api_admin_info, {
            params: {
                eid: params.eid || Config.EID,
                access_token: Config.TOKEN,
            }
        });
    }

    static adminRemove(params = {}){
        return HttpHelper.post(Config.api_admin_remove, {
            eid: params.eid || Config.EID,
            unionid: params.unionid,
            access_token: Config.TOKEN,
        });
    }

    static adminRemoveAll(params = {}){
        return HttpHelper.post(Config.api_admin_remove_all, {
            eid: params.eid || Config.EID,
            unionid: params.unionid,
            type: params.type,
            access_token: Config.TOKEN,
        });
    }

    static adminTransfer(params = {}){
        return HttpHelper.post(Config.api_admin_transfer, {
            eid: params.eid || Config.EID,
            unionid: params.unionid,
            access_token: Config.TOKEN,
        });
    }

    static adminAdd(params = {}){
        return HttpHelper.post(Config.api_admin_add, {
            eid: params.eid || Config.EID,
            target_unionid: params.target_unionid,
            type: params.type,
            admins_node: params.admins_node,
            remark: params.remark,
            access_token: Config.TOKEN,
        });
    }

    static adminEdit(params = {}){
        return HttpHelper.post(Config.api_admin_edit, {
            eid: params.eid || Config.EID,
            unionid: params.unionid,
            admins_node: params.admins_node,
            remark: params.remark,
            access_token: Config.TOKEN,
        });
    }

    // 生成添加管理员邀请二维码，参数协议与小程序 addadmin 页面一致。
    static adminQrcode(params = {}){
        return HttpHelper.post(Config.api_admin_qrcode, {
            eid: params.eid || Config.EID,
            type: params.type,
            admins_node: params.admins_node,
            access_token: Config.TOKEN,
        });
    }

    // 受邀人同意成为管理员，参数协议与小程序 shareadmin 页面一致（access_token 为受邀人自己的）。
    static adminShareAdd(params = {}){
        return HttpHelper.post(Config.api_admin_share_add, {
            share_code: params.share_code,
            admins_node: params.admins_node,
            access_token: Config.TOKEN,
        });
    }
    // 转让商户
    static toBindMch(params){
        return HttpHelper.post(Config.api_bind_mch, {
            access_token: Config.TOKEN,
            unionid: params.unionid,
            type: params.type,
            mch_id: params.mch_id,
        })
    }
    // 商户转移记录
    static getMchLog(params){
        return HttpHelper.get(Config.api_mch_rep_log, {
            params: {
                access_token: Config.TOKEN,
                page: params.page,
                count: params.count,
                mch_id: params.mch_id,
            }
        })
    }
    // 仅微信群聊可参与
    static getShareGroup(eid){
        return HttpHelper.get(Config.api_share_group, {
            params: {
                access_token: Config.TOKEN,
                eid,
            }
        })
    }
    //VIP购买的订单记录
    static getAuthOrder(unionid){
        return HttpHelper.get(Config.api_auth_order, {
            params: {
                access_token: Config.TOKEN,  
                unionid          
            }
        })        
    }
    // 回收站中恢复某个活动的数据
    static toBakPay(eid){
        const params = {
            access_token: Config.TOKEN,
            eid,
        }
        if(!WxApi.isMobile() || !WxApi.isWeixn()){ //若为pc端，则调网页支付
            params.pay_source = 'pc'
        }
        return HttpHelper.post(Config.api_bak_pay, params)
    }
    // 账号安全校验code
    static getRiskCode(unionid){
        return HttpHelper.get(Config.api_risk_qrcode, {
            params: {                
                unionid          
            }
        })   
    }    
    // 回收站复制创建活动
    static getCopyPay(eid){
        const params = {
            access_token: Config.TOKEN,
            eid,
        }
        if(!WxApi.isMobile() || !WxApi.isWeixn()){ //若为pc端，则调网页支付
            params.pay_source = 'pc'
        }
        return HttpHelper.post(Config.api_copy_pay, params)   
    }
    // 获取黑名单详情
    static getBlackDetail(unionid){
        return HttpHelper.get(Config.api_black_detail, {
            params: {                
                unionid          
            }
        })
    }
    // 提交账号申诉
    static toAppeal(params){
        return HttpHelper.post(Config.api_black_appeal, {
            access_token: Config.TOKEN,
            unionid: params.unionid,
            name: params.name,
            phone: params.phone,
            reason: params.reason,
            id_card_pic: params.id_card_pic,
            cert_pic: params.cert_pic
        })
    }
    // 获取黑名单申诉支付凭证
    static getAppealSessionKey(unionid){
        return HttpHelper.post(Config.api_apply_session_key, {
            unionid,
        })
    }
    // 付费提交账号申诉
    static toVirtualAppeal(params){
        const data = {
            access_token: params.access_token || Config.TOKEN,
            unionid: params.unionid,
            name: params.name,
            phone: params.phone,
            reason: params.reason,
            id_card_pic: params.id_card_pic,
            id_card_emblem_pic: params.id_card_emblem_pic,
            cert_pic: params.cert_pic || '',
        }
        if(!WxApi.isWeixn()){
            data.pay_source = 'pc'
        }
        return HttpHelper.post(Config.api_black_apply_pay, data)
    }
    // 获取申诉详情
    static getAppealDetail(unionid){
        return HttpHelper.get(Config.api_appeal_detail, {
           params: {unionid}
        })        
    }
    // 白名单校验
    static toCheckWhite(data){
        return HttpHelper.post(Config.api_check_white, {
            access_token: Config.TOKEN,
            eid: Config.EID,
            info: data,
        })        
    }
    // 付款记录
    static getRecordPay(data){
        return HttpHelper.get(Config.api_record_pay, {
            params: {
                access_token: Config.TOKEN,
                page: data.page,
                count: data.count,
                type: data.type,
            }
        })
    }
    // 订单投诉列表
    static getMyComplaintList(data){
        return HttpHelper.get(Config.api_my_complaint_list, {
            params: {
                access_token: Config.TOKEN,
                page: data.page,
                count: data.count,
            }
        })
    }

    // 商户投诉列表
    static getComplaintData(data){
        return HttpHelper.get(Config.api_my_complaint, {
            params: {
                access_token: Config.TOKEN,
                page: data.page,
                count: data.count,
            }
        })
    }

    // 商户投诉详情
    static getComplaintHistory(complaint_log_id){
        return HttpHelper.get(Config.api_complaint_history, {
            params: {
                access_token: Config.TOKEN,
                complaint_log_id,       
            }
        })        
    }
    
    // 新用户提交时保存头像昵称
    static saveNewUser(data){
        return HttpHelper.post(Config.api_save_new, {
            access_token: Config.TOKEN,              
            uname: data.uname,
        })
    }

    // 导出pdf数据
    static getPdfImg(info_id, eid){
        return HttpHelper.post(Config.api_pdf_export, {
            access_token: Config.TOKEN,
            eid: eid ? eid : Config.EID,
            info_id
        })
    }

    // 导出报名数据（纯文本XLS / 发送到邮箱）
    static exportEnrollData(params){
        return HttpHelper.post(Config.api_export, Object.assign({
            access_token: Config.TOKEN,
            eid: Config.EID,
            extend_type: [],
            from: 'h5',
        }, params))
    }

    // 导出纯文本+内嵌图片
    static exportEnrollExcelImg(params){
        return HttpHelper.post(Config.api_export_img_excel, Object.assign({
            access_token: Config.TOKEN,
            eid: Config.EID,
            extend_type: [],
            from: 'h5',
        }, params))
    }

    // 过去24小时下载记录
    static getExportList(){
        return HttpHelper.get(Config.api_export_list, {
            params: {
                access_token: Config.TOKEN,
                eid: Config.EID,
                page: 1,
                count: 100,
            }
        })
    }

    // 图片/签名/文件打包导出（发送到邮箱）
    static saveExportAttach(params){
        return HttpHelper.post(Config.api_export_attach_save, Object.assign({
            access_token: Config.TOKEN,
            eid: Config.EID,
        }, params))
    }

    // 邀请统计列表（分享人数/提交数/邀请明细）对齐小程序 getReferList
    static getReferList(page){
        return HttpHelper.get(Config.api_stat_refer_list, {
            params: {
                access_token: Config.TOKEN,
                eid: Config.EID,
                page: page || 1,
            }
        })
    }

    // 邀请统计导出人数 对齐小程序 subpackage/export 页 type=invite（type=1 导出人数）
    static exportInviteData(params){
        return HttpHelper.post(Config.api_export_invite, Object.assign({
            access_token: Config.TOKEN,
            eid: Config.EID,
            type: 1,
        }, params))
    }

    // 删除活动（删除原因会展示给参与者，删除后可在回收站查看）
    static removeEnroll(final_word){
        return HttpHelper.post(Config.api_remove_enroll, {
            access_token: Config.TOKEN,
            eid: Config.EID,
            final_word
        })
    }

    // 开启/截止活动（对齐小程序 openOrCloseEnroll；type：1 开启，2 截止）
    static openOrCloseEnroll(type, eid){
        return HttpHelper.post(Config.api_open_close_enroll, {
            access_token: Config.TOKEN,
            eid: eid || Config.EID,
            type
        })
    }

    // 多项目活动的项目列表（导出数据用，与小程序 getMuiltItems 对齐走 v1 接口）
    static getMultiItems(){
        return HttpHelper.get(Config.api_seat_detail, {
            params: {
                eid: Config.EID,
                access_token: Config.TOKEN,
            }
        })
    }

    // 商户申请
    static getMechData(unionid){
        return HttpHelper.get(Config.api_mch_apply, {
            params: {
                access_token: Config.TOKEN,              
                unionid
            }
        })
    }
    // 获取钱包金额
    static getBalance(){
        return HttpHelper.get(Config.api_balance, {
            params: {
                access_token: Config.TOKEN,               
            }
        })
    }
    // 获取风险投诉数据
    static getMerchantStat(){
        return HttpHelper.get(Config.api_merchant_stat, {
            params: {
                access_token: Config.TOKEN,               
            }
        })
    }
    // 转移商户
    static toTransferMerchant(data){
        let params = {
            access_token: Config.TOKEN,   
            mch_id: data.mch_id,
        }
        if(!WxApi.isMobile() || !WxApi.isWeixn()){
            params.pay_source = 'pc';
        }
        return HttpHelper.post(Config.api_mch_pay, params)
    }
    // 转移商户的小程序码
    static getTransferCode(){
        return HttpHelper.get(Config.api_mch_qrcode, {
            params: {
                access_token: Config.TOKEN
            }
        }) 
    }
    // 获取验证码
    static getVerifyCode(data){        
        const params = { access_token: Config.TOKEN };
        if(data.type == 'sub_mch'){
            params.type = 'sub_mch';
            params.mch_id = data.mch_id;
            return HttpHelper.get(Config.api_verify_code, {
                params
            }) 
        }else if(data.type == 'share_infos'){
            params.type = 'share_infos';
            params.info_id = data.info_id;
            return HttpHelper.get(Config.api_verify_code, {
                params
            }) 
        }else if(data.type == 'check_code'){
            params.code = data.code;
            params.verify_str = data.verify_str;
            params.eid = data.eid;
            return HttpHelper.post(Config.api_verify_code, params) 
        }
    }
    // 校验商户是否可转移
    static toCheckMch(data){        
        return HttpHelper.get(Config.api_check_mch, {
            params: {
                access_token: Config.TOKEN,          
                mch_id: data.mch_id,
            }
        }) 
    }
    // 求名额
    static toHelpUser(params){
        return HttpHelper.post(Config.api_help_user, {
            access_token: Config.TOKEN,          
            eid: Config.EID,
            phone: params.phone,
            reason: params.reason,
        })
    }
    // 求名额列表
    static getHelpList(data){
        return HttpHelper.get(Config.api_help_list, {
            params: {
                access_token: Config.TOKEN,          
                count: data.count,
                page: data.page,
                eid: Config.EID,
            }
        }) 
    }
    // 获取订单详情    
    static getComplaintDetail(complaint_id){
        return HttpHelper.get(Config.api_complaint_cancel, {
            params: {
                access_token: Config.TOKEN,             
                complaint_id,
            }
        }) 
    }
    // 获取商户投诉订单详情
    static getMerchantInfo(complaint_id){
        return HttpHelper.get(Config.api_merchant_detail, {
            params: {
                access_token: Config.TOKEN,             
                complaint_id,
            }
        }) 
    }
    // 撤销商户投诉    
    static toCloseComplaint(complaint_id){
        return HttpHelper.post(Config.api_complaint_close, {
            access_token: Config.TOKEN,          
            complaint_id           
        })
    }
    // 撤回求名额
    static toCancelHelp(apply_id){
        return HttpHelper.post(Config.api_cancel_help, {
            access_token: Config.TOKEN,          
            apply_id           
        })
    }
    // 获取pcopen的ticket
    static getPcTicket(){
        return HttpHelper.get(Config.api_pc_ticket, {
            params: {
                access_token: Config.TOKEN,                
            }           
        })
    }
    // 会员支付
    static toPayVip(data){
        const params = {
            access_token: Config.TOKEN,           
            pay_path: 'xcx_recyle', 
            duration: data    
        }
        if(!WxApi.isMobile() || !WxApi.isWeixn()){ //若为pc端，则调网页支付
            params.pay_source = 'pc'
        }
        return HttpHelper.post(Config.api_vip_pay, params)        
    }
    // 获取价格
    static getPayPrice(){
         return HttpHelper.get(Config.api_vip_pay, {
            params: {
                access_token: Config.TOKEN,  
            }
         })   
    }
    
    //回收站内参与活动列表
    static getJoinBakList(data){
        return HttpHelper.get(Config.api_join_bak_list, {
            params: {
                access_token: Config.TOKEN,  
                page: data.page,
                count: data.count,
                keyword: data.keyword,            
            }           
        })       
    }
    // 查看该用户是参与者还是发起者
    static getUserRole(){
        return HttpHelper.get(Config.api_user_role, {
            params: {
                access_token: Config.TOKEN,  
                eid: Config.EID            
            }           
        })       
    }
    // 退出所有设备
    static toLogoutAll(){
        return HttpHelper.post(Config.api_logout_all, { access_token: Config.TOKEN })       
    }
    // h5查看链接详情
    static getLinkDetail(data){
        return HttpHelper.get(Config.api_link_detail, {
            params: {           
                link: data.link,                 
            }           
        })       
    }
    // 链接付款 
    static toLinkPay(data){
        const params = {
            access_token: Config.TOKEN,       
            link: data.link,  
            fee: data.fee,
            remark: data.remark,   
            goods_list: data.goods_list,
            from: 'enroll_h5_new',
        }       
        if(data.referer){ //邀请人
            params.referer = data.referer;
        }  
        return HttpHelper.post(Config.api_link_pay, params)                            
    }
    // 获取会员消费记录
    static getLogList(data){        
        return HttpHelper.get(Config.api_log_list, {
            params: {
                access_token: Config.TOKEN,
                card_id: data.card_id,
                vip_id: data.vip_id,
                page: data.page,
                count: data.count
            }
        })  
    }
    // 查询是否能注销商户
    static toDelMch(mch_id){    
        return HttpHelper.post(Config.api_del_mch, {
            access_token: Config.TOKEN,
            mch_id,
        }) 
    }  
    // 注销商户 or 获取商户注销状态
    static toCancelMch(data, type = 'post'){
        if(type == 'get'){
            return HttpHelper.get(Config.api_cacel_apply, {
                params: {access_token: Config.TOKEN}                 
            })
        }else{
            return HttpHelper.post(Config.api_cacel_apply, {
                access_token: Config.TOKEN,
                sub_mchid: data.sub_mchid,
                application_info: data.application_info
            })
        }
         
    }  
    // 开启浏览器消息提醒
    static openNoticeSwitch(params){
        return HttpHelper.post(Config.api_notice_switch, {
            access_token: Config.TOKEN,
            socket_msg: params.socket_msg
        })
    }
    //拒绝退款详情
    static getRejectDetail(data){
        return HttpHelper.get(Config.api_reject_detail, {
            params: {  
                access_token: Config.TOKEN,       
                info_id: data.info_id,                 
            }           
        })       
    }
    // VIP加急审核支付
    static toUrgentPay(params){
        const data = {
            access_token: Config.TOKEN,
            bind_order_no: params.urgent_no,
            act_type: params.urgent_type,
        }
        if(!WxApi.isMobile() || !WxApi.isWeixn()){ //若为pc端，则调网页支付
            data.pay_source = 'pc'
        }
        return HttpHelper.post(Config.api_urgent_pay, data)        
    }

    // 查询年度总结
    static getAnnualReport(){
        return HttpHelper.get(Config.api_annual_report, {
            params: {
                access_token: Config.TOKEN
            }
        })        
    }
    
}
